import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Copy, Plus, Shield, Trash2 } from "lucide-react";

const MCP_SCOPES = [
  "scheduler:read",
  "scheduler:write",
  "graphics:read",
  "graphics:write",
  "video:read",
  "video:write",
  "campaigns:read",
  "campaigns:write",
  "campaigns:launch",
  "offer:read",
  "offer:write",
];

/** Realne wydawanie budżetu (kampanie ACTIVE) — tylko po świadomym zaznaczeniu w formularzu. */
const CAMPAIGNS_ACTIVATE_SCOPE = "campaigns:activate";

/** Sentinel for "no client — key acts as the issuer" (Radix Select forbids ""). */
const OWN_ACCOUNT = "__own__";

type McpApiKey = {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  client_id: string | null;
};

type ClientOption = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  client_name: string | null;
};

function clientLabel(client: ClientOption): string {
  const fullName = [client.first_name, client.last_name].filter(Boolean).join(" ");
  const person = fullName ? `${fullName} (${client.email})` : client.email;
  return client.client_name ? `${client.client_name} — ${person}` : person;
}

function isMcpKey(key: McpApiKey): boolean {
  return key.scopes?.some(
    (scope) =>
      scope.startsWith("scheduler:") ||
      scope.startsWith("graphics:") ||
      scope.startsWith("video:") ||
      scope.startsWith("campaigns:")
  );
}

function buildMcpAddCommand(apiKey: string): string {
  return `claude mcp add --transport http vp ${window.location.origin}/api/mcp --header "X-API-Key: ${apiKey}"`;
}

/**
 * McpKeyPanel — dedicated Claude MCP key management for the Integrations Hub.
 * Creates scheduler-scoped API keys (optionally in an agency client's context —
 * the key then sees that client's connected social accounts) and shows the
 * ready-to-paste `claude mcp add` command.
 */
export function McpKeyPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const { toast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string>(OWN_ACCOUNT);
  const [allowActivate, setAllowActivate] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const { data: keysData, isLoading } = useQuery<{ keys: McpApiKey[] }>({
    queryKey: ["/api/external/keys"],
  });

  // Client users for the key-context dropdown (403 for non-admins → selector hidden)
  const { data: clientsData } = useQuery<{ clients: ClientOption[] }>({
    queryKey: ["/api/external/keys/clients"],
    retry: false,
  });
  const clientOptions = clientsData?.clients ?? [];
  const clientById = new Map(clientOptions.map((client) => [client.id, client]));

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; scopes: string[]; client_id?: string }) => {
      return await apiRequest("POST", "/api/external/keys", data);
    },
    onSuccess: (data: { api_key: string }) => {
      setGeneratedKey(data.api_key);
      setShowCreateForm(false);
      setNewKeyName("");
      setSelectedClientId(OWN_ACCOUNT);
      setAllowActivate(false);
      queryClient.invalidateQueries({ queryKey: ["/api/external/keys"] });
      toast({ title: t("mcpKeys.toast.created"), description: t("mcpKeys.toast.createdDesc") });
    },
    onError: () => {
      toast({
        title: t("apiKeys.toast.tryAgain"),
        description: t("apiKeys.toast.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/external/keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external/keys"] });
      toast({ title: t("apiKeys.toast.revoked") });
    },
    onError: () => {
      toast({
        title: t("apiKeys.toast.tryAgain"),
        description: t("apiKeys.toast.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const mcpKeys = (keysData?.keys ?? []).filter((key) => !key.revoked_at && isMcpKey(key));

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: t("apiKeys.toast.copied") });
  };

  return (
    <div className="space-y-3">
      {/* Generated key banner */}
      {generatedKey && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm font-medium">{t("apiKeys.newKeyBanner")}</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/50 text-green-300 text-xs px-3 py-2 rounded font-mono break-all">
              {generatedKey}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(generatedKey)}
              className="border-green-500/50 text-green-400 hover:bg-green-900/50"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
          <p className="text-green-400/60 text-[10px] mt-2">{t("apiKeys.newKeyHint")}</p>
          <div className="mt-3 pt-3 border-t border-green-500/30">
            <p className="text-green-400 text-xs font-medium mb-1">{t("apiKeys.mcpSetupTitle")}</p>
            <p className="text-green-400/60 text-[10px] mb-2">{t("apiKeys.mcpSetupHint")}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-black/50 text-green-300 text-[10px] px-3 py-2 rounded font-mono break-all">
                {buildMcpAddCommand(generatedKey)}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(buildMcpAddCommand(generatedKey))}
                className="border-green-500/50 text-green-400 hover:bg-green-900/50"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreateForm ? (
        <div className="bg-white/5 rounded-lg p-3 border border-white/10 space-y-3">
          <div>
            <label className="text-white/50 text-xs mb-1 block">{t("apiKeys.form.name")}</label>
            <Input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder={t("mcpKeys.namePlaceholder")}
              className="bg-black/50 border-white/20 text-white text-sm"
            />
          </div>
          {clientOptions.length > 0 && (
            <div>
              <label className="text-white/50 text-xs mb-1 block">{t("apiKeys.form.client")}</label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger className="bg-black/50 border-white/20 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OWN_ACCOUNT}>{t("apiKeys.form.clientOwn")}</SelectItem>
                  {clientOptions.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {clientLabel(client)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-white/40 text-[10px] mt-1">{t("apiKeys.form.clientHint")}</p>
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowActivate}
              onChange={(e) => setAllowActivate(e.target.checked)}
              className="mt-0.5 accent-orange-600"
            />
            <span className="text-xs text-white/60">
              {t("mcpKeys.allowActivate")}
              <span className="block text-[10px] text-orange-400/70 mt-0.5">
                {t("mcpKeys.allowActivateHint")}
              </span>
            </span>
          </label>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() =>
                createMutation.mutate({
                  name: newKeyName,
                  scopes: allowActivate ? [...MCP_SCOPES, CAMPAIGNS_ACTIVATE_SCOPE] : MCP_SCOPES,
                  ...(selectedClientId !== OWN_ACCOUNT ? { client_id: selectedClientId } : {}),
                })
              }
              disabled={!newKeyName.trim() || createMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
            >
              {createMutation.isPending ? t("apiKeys.form.creating") : t("apiKeys.form.create")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowCreateForm(false)}
              className="text-white/50 text-xs"
            >
              {t("apiKeys.form.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          onClick={() => {
            setShowCreateForm(true);
            setGeneratedKey(null);
          }}
          className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          {t("mcpKeys.newKey")}
        </Button>
      )}

      {/* Active MCP keys */}
      {isLoading ? (
        <p className="text-white/30 text-xs">{t("apiKeys.loading")}</p>
      ) : mcpKeys.length === 0 ? (
        <p className="text-white/30 text-xs">{t("mcpKeys.noKeys")}</p>
      ) : (
        <div className="space-y-2">
          {mcpKeys.map((key) => {
            const keyClient = key.client_id ? clientById.get(key.client_id) : undefined;
            return (
              <div
                key={key.id}
                className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-white/10"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{key.name}</span>
                    <Badge className="text-[10px] bg-green-600/20 text-green-400 border-green-500/50">
                      {t("apiKeys.activeBadge")}
                    </Badge>
                    <Badge className="text-[10px] bg-white/10 text-white/60 border-white/20">
                      {key.client_id
                        ? keyClient
                          ? clientLabel(keyClient)
                          : t("mcpKeys.clientContext")
                        : t("apiKeys.form.clientOwn")}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-white/30 text-[10px] font-mono">
                      gfy_{key.key_prefix}...
                    </span>
                    {key.last_used_at && (
                      <span className="text-white/30 text-[10px]">
                        {t("apiKeys.lastUsed", {
                          date: new Date(key.last_used_at).toLocaleDateString(locale),
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (confirm(t("apiKeys.revokeConfirm"))) {
                      revokeMutation.mutate(key.id);
                    }
                  }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
